// Lib
import { useDispatch } from 'react-redux';
import { List } from 'immutable';

// Utils
import { getLabels } from '../../../../../../../common/elements/utils/elementPropertyUtils';

// Actions
import { removeLabelFromElements } from '../../../../../../element/labels/elementLabelActions';
import { addLabelToElementsThunk, createAndAddLabelToElementsThunk } from '../labelThunks';

// Types
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { Label } from '../../../../../../../common/labels/labelTypes';
import { LabelsToolContentProps } from '../labelComponentPropTypes';

type ReturnType = {
    handleToggleLabelSelection: (label: Label) => void;
    handleCreateLabel: (rootBoardId?: string) => void;
    handleSearchSubmit: () => void;
};

const useLabelHandlers = (
    props: LabelsToolContentProps,
    searchString: string,
    setSearchString: (searchString: string) => void,
    elements: List<ImMNElement>,
    allLabels: Label[],
): ReturnType => {
    const { currentBoardId, selectedElements, currentUserId, shareRootPath } = props;

    const dispatch = useDispatch();

    const hasLabelOnAllElements = (labelId: string) =>
        elements.every((element) => getLabels(element)?.includes(labelId));

    const handleAddLabelToElements = (label: Label) => {
        dispatch(addLabelToElementsThunk(label, elements, currentBoardId, shareRootPath));
    };

    const handleToggleLabelSelection = (label: Label) => {
        if (hasLabelOnAllElements(label.id)) {
            dispatch(removeLabelFromElements(elements, label.id));
        } else {
            handleAddLabelToElements(label);
        }
    };

    const handleCreateLabel = (rootBoardId = currentBoardId) => {
        if (!searchString) return;

        const labelData = {
            name: searchString,
            rootBoardIds: [rootBoardId],
            creator: currentUserId,
        };

        dispatch(createAndAddLabelToElementsThunk(labelData, selectedElements, currentBoardId, shareRootPath));

        setSearchString('');
    };

    const handleSearchSubmit = () => {
        const existingLabel = allLabels.find((label) => label.name.toLowerCase() === searchString.toLowerCase());

        if (!existingLabel) {
            handleCreateLabel();
            return;
        }

        if (!hasLabelOnAllElements(existingLabel.id)) handleAddLabelToElements(existingLabel);
    };

    return {
        handleToggleLabelSelection,
        handleCreateLabel,
        handleSearchSubmit,
    };
};

export default useLabelHandlers;
